import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { concat, fn } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import didUpdate from "@ember/render-modifiers/modifiers/did-update";
import { service } from "@ember/service";
import { ajax } from "discourse/lib/ajax";
import { and, eq, not } from "discourse/truth-helpers";
import DButton from "discourse/ui-kit/d-button";
import DUserLink from "discourse/ui-kit/d-user-link";
import dAvatar from "discourse/ui-kit/helpers/d-avatar";
import dFormatDate from "discourse/ui-kit/helpers/d-format-date";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";
import CommunityMediaUserDisplayName from "./community-media-user-display-name";

const TYPES = ["all", "image", "video", "gif"];
const SORTS = ["latest", "hot"];

export default class CommunityMediaGallery extends Component {
  @service router;

  @tracked section;
  @tracked items = [];
  @tracked loading = true;
  @tracked error;
  @tracked hasMore = false;

  requestId = 0;

  constructor() {
    super(...arguments);
    void this.load();
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.requestId++;
  }

  get outletArgs() {
    return this.args.outletArgs || {};
  }

  get isCreator() {
    return !this.outletArgs.game;
  }

  get sectionKey() {
    return this.isCreator
      ? "creator.resources"
      : `game.${this.outletArgs.game}.media`;
  }

  get type() {
    return TYPES.includes(this.outletArgs.type) ? this.outletArgs.type : "all";
  }

  get sort() {
    return SORTS.includes(this.outletArgs.sort)
      ? this.outletArgs.sort
      : "latest";
  }

  get page() {
    const page = Number.parseInt(this.outletArgs.page, 10);
    return Number.isInteger(page) && page >= 0 ? page : 0;
  }

  get filterTypeLabel() {
    return i18n(`community_media.filters.${this.type}`);
  }

  get eyebrow() {
    return i18n(
      this.isCreator
        ? "community_media.eyebrow.resource_hub"
        : "community_media.eyebrow.media_gallery"
    );
  }

  async load() {
    const requestId = ++this.requestId;
    this.loading = true;
    this.error = null;
    this.items = [];
    this.hasMore = false;

    try {
      const responses = [];
      for (let page = 0; page <= this.page; page++) {
        responses.push(
          await ajax("/community-media/items.json", {
            data: {
              section: this.sectionKey,
              type: this.type,
              sort: this.sort,
              page,
            },
          })
        );
      }

      if (requestId !== this.requestId) {
        return;
      }

      this.section = responses[0]?.section;
      this.items = responses.flatMap((response) => response.items);
      this.hasMore = responses.at(-1)?.meta.has_more || false;
    } catch {
      if (requestId === this.requestId) {
        this.error = i18n("community_media.load_error");
      }
    } finally {
      if (requestId === this.requestId) {
        this.loading = false;
      }
    }
  }

  @action
  reload() {
    void this.load();
  }

  transition(queryParams) {
    const routeName = this.isCreator
      ? "community-creator-resources"
      : "community-media";
    this.router.transitionTo(routeName, {
      queryParams: {
        ...(this.isCreator ? {} : { game: this.outletArgs.game }),
        type: this.type,
        sort: this.sort,
        page: 0,
        ...queryParams,
      },
    });
  }

  @action
  selectType(type) {
    this.transition({ type });
  }

  @action
  selectSort(sort) {
    this.transition({ sort });
  }

  @action
  loadMore() {
    this.transition({ page: this.page + 1 });
  }

  <template>
    <section
      class="community-media"
      aria-labelledby="community-media-title"
      {{didUpdate
        this.reload
        this.sectionKey
        this.type
        this.sort
        this.page
      }}
    >
      <header class="community-media__header">
        <div class="community-media__heading">
          <h1 id="community-media-title" class="community-media__title">
            {{this.section.name}}
          </h1>
          <p class="community-media__eyebrow">{{this.eyebrow}}</p>
        </div>
        <div class="community-media__intro">
          <p class="community-media__description">
            {{i18n "community_media.description"}}
          </p>
          <span class="community-media__intro-copy" aria-hidden="true"></span>
        </div>
      </header>

      <div class="community-media__panel">
        <div class="community-media__toolbar">
          <div
            class="community-media__filter-group community-media__filter-group--sort"
            role="group"
            aria-label={{i18n "community_media.sort_filter"}}
          >
            {{#each SORTS as |sort|}}
              <button
                type="button"
                class="btn community-media__filter"
                aria-pressed={{if (eq this.sort sort) "true" "false"}}
                {{on "click" (fn this.selectSort sort)}}
              >
                {{i18n (concat "community_media.filters." sort)}}
              </button>
            {{/each}}
          </div>
          <div
            class="community-media__filter-group community-media__filter-group--types"
            role="group"
            aria-label={{i18n "community_media.media_type_filter"}}
          >
            {{#each TYPES as |type|}}
              <button
                type="button"
                class="btn community-media__filter"
                aria-pressed={{if (eq this.type type) "true" "false"}}
                {{on "click" (fn this.selectType type)}}
              >
                {{i18n (concat "community_media.filters." type)}}
              </button>
            {{/each}}
          </div>
        </div>

        <p class="community-media__status" aria-live="polite">
          {{#if this.loading}}
            {{i18n "community_media.loading"}}
          {{else if this.error}}
            {{this.error}}
          {{else}}
            <span class="community-media__result-count">
              {{i18n
                "community_media.result_count"
                count=this.items.length
                type=this.filterTypeLabel
              }}
            </span>
          {{/if}}
        </p>

        {{#if this.items.length}}
          <ul class="community-media__grid">
            {{#each this.items as |item|}}
              <li class="community-media__grid-item">
                <article class="community-media-card">
                  <a class="community-media-card__link" href={{item.url}}>
                    <div class="community-media-card__preview">
                      {{#if item.preview_url}}
                        <img
                          class="community-media-card__image"
                          src={{item.preview_url}}
                          alt=""
                          width={{item.preview_width}}
                          height={{item.preview_height}}
                          loading="lazy"
                        />
                      {{else}}
                        <div class="community-media-card__placeholder">
                          {{dIcon (if (eq item.media_type "video") "video" "image")}}
                          <span>{{i18n "community_media.preview_unavailable"}}</span>
                        </div>
                      {{/if}}
                      {{#if (eq item.media_type "video")}}
                        <span class="community-media-card__play" aria-hidden="true">
                          {{dIcon "play"}}
                        </span>
                      {{else if (eq item.media_type "gif")}}
                        <span class="community-media-card__badge">GIF</span>
                      {{/if}}
                    </div>
                  </a>
                  <div class="community-media-card__body">
                    <a
                      class="community-media-card__title-link"
                      href={{item.url}}
                    >
                      <h2 class="community-media-card__title">{{item.title}}</h2>
                    </a>
                    <div class="community-media-card__meta">
                      <DUserLink
                        @user={{item.author}}
                        class="community-media-card__author"
                      >
                        {{dAvatar item.author imageSize="tiny" loading="lazy"}}
                        <span class="community-media-card__author-details">
                          <span class="community-media-card__username">
                            <CommunityMediaUserDisplayName
                              @user={{item.author}}
                            />
                          </span>
                          <span class="community-media-card__date">
                            {{dFormatDate item.created_at format="tiny"}}
                          </span>
                        </span>
                      </DUserLink>
                      <span class="community-media-card__replies">
                        {{dIcon "comment"}}
                        {{item.reply_count}}
                      </span>
                    </div>
                  </div>
                </article>
              </li>
            {{/each}}
          </ul>
        {{else if (and (not this.loading) (not this.error))}}
          <div class="community-media__empty">
            <h2>{{i18n "community_media.empty_title"}}</h2>
            <p>{{i18n "community_media.empty_description"}}</p>
          </div>
        {{/if}}

        {{#if this.hasMore}}
          <DButton
            class="community-media__load-more"
            @action={{this.loadMore}}
            @translatedLabel={{i18n "community_media.load_more"}}
          />
        {{/if}}
      </div>
    </section>
  </template>
}
